import React, { useState, useRef } from "react"

function SotaPlan() {
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [currentFolder, setCurrentFolder] = useState("Root")
  const fileInputRef = useRef(null)
  const fallbackInputRef = useRef(null)

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    handleFiles(files)
  }

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files)
    handleFiles(files)
  }

  const handleFiles = (files) => {
    const validFiles = files.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase()
      return extension === 'plan'
    })
    setSelectedFiles(prev => [...prev, ...validFiles])
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const handleFolderClick = (folderName) => {
    setCurrentFolder(folderName)
  }

  return (
    <div id="sota-plan-wrapper" className="w-full mb-2">
      <div id="sota-plan-container" className="py-4 px-6 bg-white dark:bg-gray-900 border border-gray-200 rounded-md">
        <div id="sota-plan-content">
          <div id="sota-plan-header" className="mb-4">
            <h2 id="sota-plan-title" className="font-semibold text-md text-gray-900 dark:text-gray-100 mb-0">
              Plan
            </h2>
            <p id="sota-plan-subtitle" className="text-gray-600 dark:text-gray-400 text-sm mt-0">
              Plan
            </p>
          </div>
          <div id="sota-plan-body" className="relative">
            <div id="sota-plan-upload-area" className="flex flex-col items-center w-full text-center">
              <div id="sota-plan-dropzone" className="w-full">
                <div
                  id="sota-plan-dropzone-inner"
                  role="presentation"
                  tabIndex={0}
                  className={`relative flex flex-col items-center justify-center w-full p-2 border-2 border-dashed rounded-md cursor-pointer transition-colors duration-200 ease-in-out ${
                    isDragging
                      ? "border-indigo-400 bg-indigo-50 text-indigo-600"
                      : "border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-600 bg-gray-50 dark:bg-gray-950 dark:bg-gray-950"
                  }`}
                  aria-disabled={false}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleClick}
                >
                  <input
                    ref={fileInputRef}
                    tabIndex={-1}
                    type="file"
                    accept=".plan"
                    onChange={handleFileInput}
                    className="sr-only"
                    style={{
                      border: "0px",
                      clip: "rect(0px, 0px, 0px, 0px)",
                      clipPath: "inset(50%)",
                      height: "1px",
                      margin: "0px -1px -1px 0px",
                      overflow: "hidden",
                      padding: "0px",
                      position: "absolute",
                      width: "1px",
                      whiteSpace: "nowrap"
                    }}
                  />
                  <div id="sota-plan-dropzone-content" className="text-center">
                    <p id="sota-plan-dropzone-title" className="text-sm font-semibold mb-0">
                      Drop your file here
                    </p>
                    <p id="sota-plan-dropzone-subtitle" className="text-xs text-gray-500 mt-0">
                      Drag & drop or click to select file
                    </p>
                    <div id="sota-plan-file-icons" className="mt-2 flex flex-wrap items-center justify-center space-x-1">
                      <div id="sota-plan-icon">
                        <div
                          className="flex flex-col overflow-hidden shadow-md transition-all duration-200 hover:shadow-lg"
                          style={{ width: "24px", height: "auto", borderRadius: "4.8px" }}
                        >
                          <div
                            className="flex items-center justify-center w-full bg-white dark:bg-gray-900"
                            style={{
                              height: "19.2px",
                              padding: "2.4px",
                              backgroundColor: "rgb(222, 222, 222)"
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14.4"
                              height="14.4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#D32F2F"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="bg-transparent"
                            >
                              <path d="M8 2v4"></path>
                              <path d="M16 2v4"></path>
                              <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                              <path d="M3 10h18"></path>
                            </svg>
                          </div>
                          <div
                            className="w-full flex items-center justify-center"
                            style={{
                              height: "9.6px",
                              backgroundColor: "rgb(211, 47, 47)"
                            }}
                          >
                            <span
                              className="text-center font-medium"
                              style={{
                                color: "rgb(232, 232, 232)",
                                width: "100%",
                                justifyContent: "center",
                                alignItems: "center",
                                fontSize: "5.4px"
                              }}
                            >
                              PLAN
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <input
                  id="sota-plan-file-upload-fallback"
                  ref={fallbackInputRef}
                  type="file"
                  name="file-upload-fallback-ID:Plan-single"
                  accept=".plan"
                  onChange={handleFileInput}
                  className="sr-only"
                  style={{
                    position: "absolute",
                    width: "1px",
                    height: "1px",
                    padding: "0px",
                    margin: "-1px",
                    overflow: "hidden",
                    clip: "rect(0px, 0px, 0px, 0px)",
                    whiteSpace: "nowrap",
                    borderWidth: "0px"
                  }}
                />
              </div>
              <span id="sota-plan-or-separator" className="m-1 text-gray-500 font-semibold text-sm">
                Or
              </span>
              <div id="sota-plan-folder-file-selector" className="w-full flex flex-row gap-2">
                <div id="sota-plan-folder-selector" className="flex-1 h-full scrollbar-hide">
                  <div
                    id="sota-plan-folder-list"
                    className="h-[122px] overflow-auto rounded-md p-2 border flex flex-col gap-1"
                  >
                    <div id="sota-plan-folder-label" className="text-gray-500 font-medium text-xs italic text-left">
                      Pick a Folder
                    </div>
                    <div id="sota-plan-folder-root" className="flex items-center text-xs bg-gray-100 dark:bg-gray-800 dark:bg-gray-900 px-2 py-1 rounded mb-1 flex-wrap">
                      <div className="flex items-center">
                        <button
                          id="sota-plan-folder-root-button"
                          className="text-gray-700 dark:text-gray-300 hover:text-blue-600 hover:underline font-medium"
                          onClick={() => handleFolderClick("Root")}
                        >
                          Root
                        </button>
                      </div>
                    </div>
                    <div id="sota-plan-folder-dam-select">
                      <div>
                        <div
                          id="sota-plan-folder-dam-select-button"
                          className="cursor-pointer flex justify-between relative rounded-md overflow-hidden group relative hover:bg-gradient-to-r hover:from-gray-200 hover:to-gray-50 hover:ring-2 hover:ring-offset-2 hover:ring-gray-600 transition-all ease-out duration-300 text-gray-700 dark:text-gray-300 border-l-4 hover:bg-gradient-to-l flex lg:gap-x-2 gap-x-1 rounded-md p-1 text-sm font-medium w-full items-center min-w-0 hover:bg-gradient-to-l hover:from-yellow-50 hover:to-white hover:text-yellow-600 hover:border-yellow-500"
                          onClick={() => handleFolderClick("DAM Select")}
                        >
                          <span className="absolute right-0 w-8 h-32 -mt-12 transition-all duration-500 transform translate-x-12 bg-gray-400 opacity-10 rotate-12 group-hover:-translate-x-40 ease"></span>
                          <div className="flex items-center gap-x-2">
                            <img
                              alt="corpus"
                              className="h-9 w-9 text-gray-600 dark:text-gray-400"
                              src="https://upload.wikimedia.org/wikipedia/commons/a/a1/Icons8_flat_folder.svg"
                            />
                            <div className="text-xs flex flex-col justify-center text-left">
                              <p className="font-medium text-gray-800">DAM Select</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div></div>
                    <div></div>
                  </div>
                </div>
                <div id="sota-plan-file-selector" className="flex-1 h-full scrollbar-hide">
                  <div
                    id="sota-plan-file-list"
                    className="h-[34px] overflow-auto rounded-md p-2 border flex flex-col gap-1"
                  >
                    <div id="sota-plan-file-label" className="text-gray-500 font-medium text-xs italic text-left">
                      Pick a File
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SotaPlan

